import { observer, useLocalObservable } from "mobx-react-lite";
import { type Dispatch, type FC, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import {
  BRANCH_MEDIA_ACCEPTED_IMAGE_TYPES,
  type MediaItem,
  RECEIPT_MEDIA_VALIDATION,
} from "@/api/admin/branches";
import { commonApi } from "@/api/common";
import { ErrorForm, SimpleText } from "@/components/atoms";
import { Form } from "@/components/ui/form";

import { MediaStore, MediaUploader, UploadedFiles } from "../../MediaUploader";

interface ReceiptDefaultValues {
  billNumberMedia: MediaItem[];
  orderTotalMedia: MediaItem[];
}

interface Props {
  id: string;
  defaultValues: ReceiptDefaultValues;
  onPendingChange: Dispatch<React.SetStateAction<boolean>>;
  onSubmit: (payload: {
    branchReceiptBillNumberMediaItemId: string | null | undefined;
    branchReceiptOrderTotalMediaItemId: string | null | undefined;
  }) => void;
  onClose?: () => void;
}

const toMediaFiles = (media: MediaItem[]) =>
  media.map(m => ({
    id: m.mediaId,
    previewUrl: m.variants?.[0]?.url ?? "",
    type: m.type,
  }));

const isUnchanged = (store: MediaStore, initialIds: string[]) => {
  const currentIds = store.mediaFiles.map(m => m.id);
  return (
    currentIds.length === initialIds.length &&
    currentIds.every((mediaId, i) => mediaId === initialIds[i])
  );
};

const ReceiptPicture: FC<Props> = observer(
  ({ id, defaultValues, onPendingChange, onSubmit, onClose }) => {
    const form = useForm({ defaultValues, mode: "onChange" });

    const initialBillNumberIds = useRef<string[]>([]);
    const initialOrderTotalIds = useRef<string[]>([]);

    const billNumberStore = useLocalObservable(
      () => new MediaStore(undefined, RECEIPT_MEDIA_VALIDATION)
    );
    const orderTotalStore = useLocalObservable(
      () => new MediaStore(undefined, RECEIPT_MEDIA_VALIDATION)
    );

    const [errorBillNumberMedia, setErrorBillNumberMedia] = useState<
      string | null
    >(null);
    const [errorOrderTotalMedia, setErrorOrderTotalMedia] = useState<
      string | null
    >(null);

    useEffect(() => {
      if (defaultValues?.billNumberMedia?.length) {
        const mediaFiles = toMediaFiles(defaultValues.billNumberMedia);
        billNumberStore.restoreFromPayload(mediaFiles);
        initialBillNumberIds.current = mediaFiles.map(m => m.id!);
      }
      if (defaultValues?.orderTotalMedia?.length) {
        const mediaFiles = toMediaFiles(defaultValues.orderTotalMedia);
        orderTotalStore.restoreFromPayload(mediaFiles);
        initialOrderTotalIds.current = mediaFiles.map(m => m.id!);
      }
    }, [defaultValues, billNumberStore, orderTotalStore]);

    const resolveMediaId = async (
      store: MediaStore,
      initialIds: string[]
    ): Promise<string | null | undefined> => {
      if (isUnchanged(store, initialIds)) {
        return store.mediaFiles[0]?.id ?? null;
      }

      if (store.mediaFiles.length === 0) {
        return null;
      }

      const mediaFile = store.mediaFiles[0];
      if (mediaFile.file) {
        const response = await commonApi.mediaUpload(mediaFile.file);
        return response.id;
      }

      return mediaFile.id;
    };

    const handleSubmit = async () => {
      const billNumberUnchanged = isUnchanged(
        billNumberStore,
        initialBillNumberIds.current
      );
      const orderTotalUnchanged = isUnchanged(
        orderTotalStore,
        initialOrderTotalIds.current
      );

      if (billNumberUnchanged && orderTotalUnchanged) {
        onClose?.();
        return;
      }

      try {
        onPendingChange(true);
        setErrorBillNumberMedia(null);
        setErrorOrderTotalMedia(null);

        const [
          branchReceiptBillNumberMediaItemId,
          branchReceiptOrderTotalMediaItemId,
        ] = await Promise.all([
          resolveMediaId(billNumberStore, initialBillNumberIds.current),
          resolveMediaId(orderTotalStore, initialOrderTotalIds.current),
        ]);

        onSubmit({
          branchReceiptBillNumberMediaItemId,
          branchReceiptOrderTotalMediaItemId,
        });
      } catch (error) {
        setErrorBillNumberMedia("Failed to upload receipt image.");
      } finally {
        onPendingChange(false);
      }
    };

    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} id={id}>
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-5">
              <SimpleText>
                <span className="font-bold">Photo requirements:</span> use a
                JPG, PNG or GIF. Keep it under 3 MB for faster loading.
                Dimensions: Image (700X700px).
              </SimpleText>

              <div className="flex items-center gap-8">
                <div className="flex flex-col gap-3">
                  <SimpleText classes="font-semibold">
                    Bill Number photo:
                  </SimpleText>
                  <div className="flex gap-2 flex-wrap">
                    {billNumberStore.mediaFiles.map(media => (
                      <UploadedFiles
                        key={media.id}
                        media={media}
                        index={billNumberStore.mediaFiles.findIndex(
                          m => m.id === media.id
                        )}
                        mediaStore={billNumberStore}
                      />
                    ))}
                    {!billNumberStore.isTotalLimitReached && (
                      <MediaUploader
                        acceptedTypes={BRANCH_MEDIA_ACCEPTED_IMAGE_TYPES}
                        disabled={billNumberStore.isTotalLimitReached}
                        mediaStore={billNumberStore}
                        onError={setErrorBillNumberMedia}
                      />
                    )}
                  </div>

                  {errorBillNumberMedia && (
                    <ErrorForm message={errorBillNumberMedia} />
                  )}
                </div>

                <div className="flex flex-col gap-3">
                  <SimpleText classes="font-semibold">
                    Order Total photo:
                  </SimpleText>
                  <div className="flex gap-2 flex-wrap">
                    {orderTotalStore.mediaFiles.map(media => (
                      <UploadedFiles
                        key={media.id}
                        media={media}
                        index={orderTotalStore.mediaFiles.findIndex(
                          m => m.id === media.id
                        )}
                        mediaStore={orderTotalStore}
                      />
                    ))}
                    {!orderTotalStore.isTotalLimitReached && (
                      <MediaUploader
                        acceptedTypes={BRANCH_MEDIA_ACCEPTED_IMAGE_TYPES}
                        disabled={orderTotalStore.isTotalLimitReached}
                        mediaStore={orderTotalStore}
                        onError={setErrorOrderTotalMedia}
                      />
                    )}
                  </div>

                  {errorOrderTotalMedia && (
                    <ErrorForm message={errorOrderTotalMedia} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </form>
      </Form>
    );
  }
);

export default ReceiptPicture;
